import { useMutation, useQueryClient } from 'react-query'
import { BASE_API_URL } from 'constants/env'
import { API_METHODS } from 'constants/api-methods'
import { apiHeaders } from 'utils/api-headers'
import { store } from 'index'
import { handleErrors } from 'utils/handle-errors'
import { usersQueryKeys } from 'utils/query-keys'
import toast from 'react-hot-toast'

interface Params {}

interface ApiError extends Error {
  response?: {
    data: {
      errors?: string[]
      error?: {
        messages?: string[]
      }
      message?: string
    }
  }
}

const sendUpdateUser = async (uuid: string, apiParams: Params) => {
  const response = await fetch(
    `${BASE_API_URL}/admin/api/v1/users/${uuid}/update`,
    {
      method: API_METHODS.PUT,
      headers: apiHeaders(store.getState()),
      body: JSON.stringify(apiParams),
    }
  )
  const json = await response.json()

  if (!response.ok) {
    // Throw error with server response data
    const error: ApiError = new Error('Update failed')
    error.response = { data: json }
    throw error
  }

  return json
}

const useUpdateUser = (uuid: string) => {
  const queryClient = useQueryClient()
  return useMutation(
    ({
      name,
      email,
      phone_number,
      is_pro,
      linq_internal_admin,
      can_have_unlimited_pages,
      stripe_user_id,
      entitlement_grouping_id,
      pro_plus,
      internal_slack_id,
    }: {
      name?: string
      email?: string
      phone_number?: string
      is_pro?: boolean
      linq_internal_admin?: boolean
      can_have_unlimited_pages?: boolean
      stripe_user_id?: null
      entitlement_grouping_id?: number
      pro_plus?: boolean
      internal_slack_id?: string
    }) => {
      const apiParams = {
        name,
        email,
        phone_number,
        is_pro,
        linq_internal_admin,
        can_have_unlimited_pages,
        stripe_user_id,
        entitlement_grouping_id,
        pro_plus,
        internal_slack_id,
      }
      return sendUpdateUser(uuid, apiParams)
    },
    {
      onError: (error: ApiError) => {
        // Extract error message from server response
        if (error?.response?.data?.errors) {
          toast.error(
            `Failed to update user: ${error.response.data.errors.join(', ')}`
          )
        } else {
          toast.error(
            'Failed to update user take a screenshot of the console and send to engineers'
          )
        }
      },
      onSettled: () => {
        queryClient.invalidateQueries(usersQueryKeys.detail(uuid))
      },
    }
  )
}

export default useUpdateUser
